import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UploadSimple, Users, Package, CheckCircle, Warning, FileCsv, CloudArrowDown, ClockCounterClockwise } from "@phosphor-icons/react";

function DropZone({ label, icon: Icon, endpoint, testid, onDone }) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!/\.(csv|xlsx|xlsm)$/i.test(file.name)) {
      toast.error("Format non supporté — envoie un .csv ou .xlsx");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`${r.data.inserted} créés · ${r.data.updated} mis à jour`);
      onDone && onDone(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`rounded-2xl border transition-colors ${
        dragOver ? "border-[#005CFF] bg-[#005CFF]/10" : "border-white/10 bg-white/[0.03]"
      } p-4`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) upload(f);
      }}
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#005CFF]/15 border border-[#005CFF]/30 flex items-center justify-center shrink-0">
          <Icon size={20} weight="duotone" className="text-[#7CB2FF]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm">{label}</div>
          <div className="text-[11px] text-white/50 mt-1">
            Glisse-dépose ton export Axonaut (.csv ou .xlsx) ou clique pour choisir
          </div>
          <Button
            className="mt-3 h-9 bg-[#005CFF] hover:bg-[#0047CC] text-xs"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            data-testid={`${testid}-btn`}
          >
            <UploadSimple size={14} weight="bold" /> {busy ? "Import en cours…" : "Choisir un fichier"}
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,.xlsm"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
          />
        </div>
      </div>

      {result && (
        <div className="mt-4 rounded-xl bg-black/40 border border-white/5 p-3 text-xs space-y-2" data-testid={`${testid}-result`}>
          <div className="flex items-center gap-2 font-semibold text-emerald-400">
            <CheckCircle size={14} weight="fill" /> Import terminé — {result.total_rows} lignes
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Créés" value={result.inserted} color="#10B981" />
            <Stat label="Mis à jour" value={result.updated} color="#005CFF" />
            <Stat label="Ignorés" value={result.skipped} color="#F59E0B" />
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-2 space-y-0.5 max-h-24 overflow-y-auto">
              <div className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1">
                <Warning size={11} /> Avertissements ({result.errors.length})
              </div>
              {result.errors.map((e, i) => (
                <div key={`err-${i}-${e.slice(0, 20)}`} className="text-[11px] text-amber-400/80">• {e}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded-lg bg-white/[0.02] border border-white/5 p-2 text-center">
      <div className="text-lg font-display font-bold tabular-nums" style={{ color }}>{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-white/50 mt-0.5">{label}</div>
    </div>
  );
}

export default function AxonautImportPanel() {
  return (
    <div className="space-y-4" data-testid="axonaut-import-panel">
      <AxonautLiveSyncCard />

      <div className="rounded-2xl bg-gradient-to-br from-[#005CFF]/15 to-transparent border border-[#005CFF]/30 p-4">
        <div className="flex items-center gap-2 mb-1">
          <FileCsv size={20} weight="duotone" className="text-[#7CB2FF]" />
          <div className="font-display font-semibold">Import manuel depuis fichier</div>
        </div>
        <div className="text-[11px] text-white/60 leading-relaxed">
          En complément (ou si tu n&apos;as pas encore configuré ta clé API), tu peux importer directement les
          exports CSV/XLSX d&apos;Axonaut.
          <br />
          <span className="text-white/40">
            Politique de fusion : <b className="text-white/70">&laquo;&nbsp;fill-blanks&nbsp;&raquo;</b> — un client déjà existant garde ses champs
            (nom, adresse, email…) et seuls les champs vides sont remplis avec les données Axonaut. Les prix matériel sont
            toujours mis à jour (Axonaut = source de vérité).
          </span>
        </div>
      </div>

      <DropZone
        label="Fichier clients Axonaut"
        icon={Users}
        endpoint="/clients/import-axonaut"
        testid="axonaut-import-clients"
      />

      <DropZone
        label="Catalogue matériel Axonaut"
        icon={Package}
        endpoint="/material-catalog/import-axonaut"
        testid="axonaut-import-material"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live sync via Axonaut REST API (uses the stored API key)
// ---------------------------------------------------------------------------
function AxonautLiveSyncCard() {
  const [syncing, setSyncing] = useState(false);
  const [last, setLast] = useState(null);
  const [runs, setRuns] = useState([]);

  const loadStatus = async () => {
    try {
      const r = await api.get("/admin/axonaut/sync-status");
      setRuns(r.data.runs || []);
      setLast(r.data.runs?.[0] || null);
    } catch { /* silent */ }
  };
  useEffect(() => { loadStatus(); }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/axonaut/sync-now");
      toast.success(`Sync OK — ${r.data.clients.inserted} clients créés, ${r.data.clients.updated} MAJ`);
      await loadStatus();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="rounded-2xl bg-gradient-to-br from-emerald-500/15 to-transparent border border-emerald-500/30 p-4" data-testid="axonaut-live-sync">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
          <CloudArrowDown size={20} weight="duotone" className="text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm">Sync live via API Axonaut</div>
          <div className="text-[11px] text-white/60 mt-1 leading-relaxed">
            Récupère clients + catalogue directement depuis Axonaut sans fichier.
            <br />
            <span className="text-white/40">
              Nécessite ta clé API renseignée dans <b>Axonaut → Clé API</b>. Programme un cron quotidien
              (endpoint <code className="text-[10px]">POST /api/cron/axonaut-sync</code>) pour synchroniser
              automatiquement chaque nuit.
            </span>
          </div>
          <Button
            className="mt-3 h-9 bg-emerald-600 hover:bg-emerald-700 text-xs"
            disabled={syncing}
            onClick={syncNow}
            data-testid="axonaut-sync-now-btn"
          >
            <CloudArrowDown size={14} weight="bold" /> {syncing ? "Sync en cours…" : "Synchroniser maintenant"}
          </Button>
        </div>
      </div>

      {last && (
        <div className="mt-4 rounded-xl bg-black/40 border border-white/5 p-3 text-xs" data-testid="axonaut-last-sync">
          <div className="flex items-center gap-2 mb-2 text-emerald-400 font-semibold">
            <CheckCircle size={14} weight="fill" /> Dernière sync — {last.finished_at ? new Date(last.finished_at).toLocaleString("fr-FR") : "en cours"}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <StatBlock title="Clients" data={last.clients} />
            <StatBlock title="Matériel" data={last.material} />
          </div>
          {last.errors?.length > 0 && (
            <div className="mt-2 space-y-0.5 max-h-24 overflow-y-auto">
              <div className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1">
                <Warning size={11} /> Erreurs ({last.errors.length})
              </div>
              {last.errors.map((e, i) => (
                <div key={`sync-err-${i}-${e.slice(0, 20)}`} className="text-[11px] text-amber-400/80 truncate">• {e}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {runs.length > 1 && (
        <details className="mt-2">
          <summary className="text-[10px] text-white/40 cursor-pointer flex items-center gap-1 hover:text-white/70">
            <ClockCounterClockwise size={11} /> Voir l&apos;historique ({runs.length} runs)
          </summary>
          <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
            {runs.map((r) => (
              <div key={r.id} className="text-[10px] text-white/50 flex justify-between border-b border-white/5 py-1">
                <span>{new Date(r.started_at).toLocaleString("fr-FR")}</span>
                <span className="tabular-nums">
                  {r.clients.inserted}+{r.clients.updated} clients · {r.material.inserted}+{r.material.updated} mat
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function StatBlock({ title, data }) {
  const d = data || {};
  return (
    <div className="rounded-lg bg-white/[0.02] border border-white/5 p-2">
      <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">{title}</div>
      <div className="grid grid-cols-3 gap-1 text-center">
        <div>
          <div className="text-sm font-bold text-emerald-400 tabular-nums">{d.inserted || 0}</div>
          <div className="text-[8px] text-white/40 uppercase tracking-widest">Créés</div>
        </div>
        <div>
          <div className="text-sm font-bold text-[#7CB2FF] tabular-nums">{d.updated || 0}</div>
          <div className="text-[8px] text-white/40 uppercase tracking-widest">MAJ</div>
        </div>
        <div>
          <div className="text-sm font-bold text-white/50 tabular-nums">{d.skipped || 0}</div>
          <div className="text-[8px] text-white/40 uppercase tracking-widest">Skip</div>
        </div>
      </div>
    </div>
  );
}
